import React, { useState } from 'react';
import axios from 'axios';
import { Form, Button, Alert } from 'react-bootstrap';
import DragDropFileUpload from './DragDropFileUpload';
import { uploadAssetFilesToS3, MAX_ASSET_FILE_BYTES } from '../utils/assetS3Upload';
import { createApiUrl } from '../utils/apiConfig';
import './UploadAsset.css';

const UploadAsset = ({ refetchData }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [video, setVideo] = useState(null);
    const [thumbnail, setThumbnail] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [resources, setResources] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // File handlers
    const handleVideoSelect = (files) => {
        // Handle both single file and file array - take the first file
        const fileArray = Array.isArray(files) ? files : [files];
        setVideo(fileArray[0] || null);
    };

    const handleVideoRemove = () => {
        setVideo(null);
    };

    const handleThumbnailSelect = (files) => {
        // Handle both single file and file array - take the first file
        const fileArray = Array.isArray(files) ? files : [files];
        setThumbnail(fileArray[0] || null);
    };

    const handleThumbnailRemove = () => {
        setThumbnail(null);
    };

    const handleDocumentsSelect = (files) => {
        // Handle both single file and file array
        const fileArray = Array.isArray(files) ? files : [files];
        setDocuments(fileArray);
    };

    const handleDocumentsRemove = () => {
        setDocuments([]);
    };

    const handleResourcesSelect = (files) => {
        // Handle both single file and file array
        const fileArray = Array.isArray(files) ? files : [files];
        setResources(fileArray);
    };

    const handleResourcesRemove = () => {
        setResources([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            setUploading(true);
            setUploadProgress(0);

            const uploaded = await uploadAssetFilesToS3({
                video,
                thumbnail,
                documents,
                resources,
                onProgress: setUploadProgress,
            });

            await axios.post(
                createApiUrl('/upload-asset/complete'),
                {
                    name,
                    description,
                    video: uploaded.video,
                    thumbnail: uploaded.thumbnail,
                    documents: uploaded.documents,
                    resources: uploaded.resources,
                },
                { withCredentials: true }
            );

            setSuccess('Asset uploaded successfully!');
            // Reset form fields
            setName('');
            setDescription('');
            setVideo(null);
            setThumbnail(null);
            setDocuments([]);
            setResources([]);
            
            // Refresh data if refetchData is provided
            if (refetchData && typeof refetchData === 'function') {
                refetchData();
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'An error occurred while uploading the asset.'
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="upload-asset-container">
            <h1>Upload Asset</h1>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            {uploading && (
                <Alert variant="info">
                    Uploading to storage… {uploadProgress}%
                    <div className="progress mt-2" style={{ height: '8px' }}>
                        <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${uploadProgress}%` }}
                            aria-valuenow={uploadProgress}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        />
                    </div>
                </Alert>
            )}
            <Form onSubmit={handleSubmit} className="form">
                <Form.Group className="mb-3" controlId="video-file">
                    <Form.Label>Video:</Form.Label>
                    <DragDropFileUpload
                        onFileSelected={handleVideoSelect}
                        onFileRemoved={handleVideoRemove}
                        selectedFile={video}
                        accept="video/*"
                        maxFileSize={MAX_ASSET_FILE_BYTES}
                        supportedTypes={['.mp4', '.avi', '.mov', '.wmv']}
                        placeholder="Click to upload video or drag and drop"
                        description="Supports: MP4, AVI, MOV, WMV (up to 8GB)"
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="thumbnail-file">
                    <Form.Label>Video Thumbnail:</Form.Label>
                    <DragDropFileUpload
                        onFileSelected={handleThumbnailSelect}
                        onFileRemoved={handleThumbnailRemove}
                        selectedFile={thumbnail}
                        accept="image/*"
                        maxFileSize={5 * 1024 * 1024} // 5MB
                        supportedTypes={['.jpg', '.jpeg', '.png', '.gif']}
                        placeholder="Click to upload thumbnail or drag and drop"
                        description="Supports: JPG, PNG, GIF"
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="documents-file">
                    <Form.Label>Documents:</Form.Label>
                    <DragDropFileUpload
                        onFileSelected={handleDocumentsSelect}
                        onFileRemoved={handleDocumentsRemove}
                        selectedFile={documents.length > 0 ? documents[0] : null}
                        accept=".pdf"
                        multiple={true}
                        maxFileSize={10 * 1024 * 1024} // 10MB
                        supportedTypes={['.pdf']}
                        placeholder="Click to upload documents or drag and drop"
                        description="Supports: PDF files"
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="resources-file">
                    <Form.Label>Resources:</Form.Label>
                    <DragDropFileUpload
                        onFileSelected={handleResourcesSelect}
                        onFileRemoved={handleResourcesRemove}
                        selectedFile={resources.length > 0 ? resources[0] : null}
                        accept=".pdf"
                        multiple={true}
                        maxFileSize={10 * 1024 * 1024} // 10MB
                        supportedTypes={['.pdf']}
                        placeholder="Click to upload resources or drag and drop"
                        description="Supports: PDF files"
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="assetName">
                    <Form.Label>Name:</Form.Label>
                    <Form.Control 
                        type="text" 
                        value={name} 
                        onChange={(e) => setName(e.target.value)} 
                        required 
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="assetDescription">
                    <Form.Label>Description:</Form.Label>
                    <Form.Control 
                        as="textarea" 
                        rows={3} 
                        value={description} 
                        onChange={(e) => setDescription(e.target.value)} 
                        required 
                    />
                </Form.Group>
                
                <div className="d-flex justify-content-between mb-3">
                    <Button variant="primary" type="submit" disabled={uploading}>
                        {uploading ? 'Uploading…' : 'Save Changes'}
                    </Button>
                    <Button variant="secondary" onClick={() => window.history.back()}>
                        Cancel
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default UploadAsset; 